import {UserOutlined} from '@ant-design/icons';
import type {Settings as LayoutSettings} from '@ant-design/pro-components';
// import {SettingDrawer} from '@ant-design/pro-components';
import type {RequestConfig, RunTimeLayoutConfig} from '@umijs/max';
import {history, useAntdConfigSetter} from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import {errorConfig} from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import {getFlatMenus, type MenuDataItem, transformRoute} from "@umijs/route-utils";
import {fetchCurrentMenus, getCurrentUser} from "@/services/system/login";
import routes from "../config/routes";
import {createStyles} from "antd-style";
import {Avatar} from "antd";
import {patchRoutes} from '@/.umi/plugin-layout/runtime';

// const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

export type InitialStateProps = {
  fetchInitData?: () => Promise<{
    currentUser?: API.User;
    flatMenus?: Record<string, MenuDataItem>;
  }>;
  settings?: Partial<LayoutSettings>;
  routePathCodeMap?: Record<string, string>;
  currentUser?: API.User;
  flatMenus?: Record<string, MenuDataItem>;
  loading?: boolean;
};

const SYSTEM_DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const LEGACY_MENU_CODE_MAP: Record<string, string> = {
  'card-templates': 'member_ops.card-templates',
  'member-card-instances': 'member_ops.member-card-instances',
  'member-videos': 'movements.member-videos',
  index: 'movements.index',
  accessories: 'movements.accessories',
  'fitness-mappings': 'movements.fitness-mappings',
};

const getSystemNavTheme = (): 'realDark' | 'light' => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia(SYSTEM_DARK_MEDIA_QUERY).matches ? 'realDark' : 'light';
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<InitialStateProps> {
  const fetchInitData = async () => {

    const convToMenuItems = (
      menus?: API.Menu[],
      parent_code?: string,
    ): MenuDataItem[] | undefined => {
      if (!menus) {
        return undefined;
      }
      const result: MenuDataItem[] = [];
      menus.forEach((menu) => {
        if (!menu.code) {
          return;
        }
        const code = parent_code ? `${parent_code}.${menu.code}` : `${menu.code}`;
        const menuItem: MenuDataItem = {
          key: code,
          path: code,
          name: menu.name,
          children: convToMenuItems(menu.children, code),
        };
        result.push(menuItem);
      });
      return result;
    };

    try {
      // 当前登录用户信息
      const userRes = await getCurrentUser();
      const currentUser = userRes.data;
      if (currentUser) {
        currentUser.statusChecked = currentUser?.status === 1;
      }

      // 当前菜单信息
      let flatMenus: Record<string, MenuDataItem> = {};
      try {
        const menus = await fetchCurrentMenus();
        if (menus.data) {
          const menuItems = convToMenuItems(menus.data);
          flatMenus = getFlatMenus(menuItems);
          // console.log("flatMenus", flatMenus);
        }
        return {currentUser, flatMenus};
      } catch (e) {
        console.error(e);
      }

      return {currentUser, flatMenus};

    } catch (_error) {
      history.push(loginPath);
    }
    return {};
  };

  const flatRouteMenus = getFlatMenus(transformRoute(routes).menuData);
  const routePathCodeMap: Record<string, string> = {};
  Object.keys(flatRouteMenus).forEach((key) => {
    const menu = flatRouteMenus[key];
    let code = menu.code;
    if (menu.pro_layout_parentKeys) {
      const codes: string[] = [];
      menu.pro_layout_parentKeys.forEach((p) => {
        codes.push(flatRouteMenus[p].code);
      });
      if (codes.length > 0) {
        code = `${codes.join('.')}.${code}`;
      }
    }
    if (menu.path) {
      routePathCodeMap[menu.path] = code;
    }
  });

  // console.log("routePathCodeMap", routePathCodeMap)

  if (history.location.pathname !== loginPath) {
    const data = await fetchInitData();
    return {
      fetchInitData: fetchInitData,
      settings: {
        ...defaultSettings,
        navTheme: getSystemNavTheme(),
      } as Partial<LayoutSettings>,
      routePathCodeMap,
      ...data,
    };
  }


  return {
    fetchInitData: fetchInitData,
    settings: {
      ...defaultSettings,
      navTheme: getSystemNavTheme(),
    } as Partial<LayoutSettings>,
    routePathCodeMap,
  };
}


const useStyles = createStyles(({token}) => {
  return {
    avatar: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});



/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // baseURL: 'https://proapi.azurewebsites.net',
  ...errorConfig,
};

/**
 * @name 运行时配置
 * @description 覆盖默认的 render 方法，添加 ConfigProvider
 */
export function render(oldRender: () => void) {
  oldRender();
}

/**
 * @name layout 配置
 * @description 全局布局配置
 */
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const { styles } = useStyles();
  const setAntdConfig = useAntdConfigSetter();

  const loopMenuItems = (menus?: MenuDataItem[]): MenuDataItem[] => {
    if (!menus || menus.length === 0) {
      return [];
    }

    const result: MenuDataItem[] = [];

    menus.forEach((menu) => {
      if (
        !initialState ||
        !initialState.routePathCodeMap ||
        !initialState.flatMenus
      ) {
        return;
      }

      const flatMenus = initialState.flatMenus;
      const code = menu.path ? initialState.routePathCodeMap[menu.path] : undefined;
      const children = loopMenuItems(menu.children);
      const permissionCodes = [
        code,
        menu.code,
        menu.code ? LEGACY_MENU_CODE_MAP[menu.code] : undefined,
      ].filter(Boolean) as string[];
      const hasPermission = permissionCodes.some((permissionCode) =>
        Object.hasOwn(flatMenus, permissionCode),
      );

      if (hasPermission || children.length > 0) {
        const menuItem: MenuDataItem = {
          ...menu,
        };
        menuItem.children = children;
        menuItem.routes = children;
        result.push(menuItem);
      }
    });

    return result;
  };

  const isDark = initialState?.settings?.navTheme === 'realDark';

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(SYSTEM_DARK_MEDIA_QUERY);
    const applySystemTheme = (darkMode: boolean) => {
      const nextTheme: 'realDark' | 'light' = darkMode ? 'realDark' : 'light';
      setInitialState((prevInitialState) => {
        if (prevInitialState?.settings?.navTheme === nextTheme) {
          return prevInitialState;
        }
        return {
          ...prevInitialState,
          settings: {
            ...prevInitialState?.settings,
            navTheme: nextTheme,
          },
        };
      });
    };

    applySystemTheme(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      const handleChange = (event: MediaQueryListEvent) => applySystemTheme(event.matches);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    const handleChange = (event: MediaQueryListEvent) => applySystemTheme(event.matches);
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [setInitialState]);

  // 使用 useAntdConfigSetter 全局设置主题，作用于整个应用包括 ProLayout
  React.useEffect(() => {
    // 设置 body 类名用于 CSS 变量
    if (isDark) {
      document.body.classList.remove('light-theme');
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
      document.body.classList.add('light-theme');
    }

    // 全局设置 Ant Design 主题配置
    setAntdConfig({
      theme: {
        token: {
          fontFamily: 'AlibabaSans, sans-serif',
          colorPrimary: '#5b8cff',
          borderRadius: 8,
          colorSuccess: '#38d9a9',
          colorWarning: '#ffd166',
          colorError: '#ff6b6b',
          colorInfo: '#5b8cff',
          ...(isDark ? {
            colorBgBase: '#151a27',
            colorBgContainer: '#1b1f2a',
            colorBgElevated: '#1b1f2a',
            colorBorder: '#262b3a',
            colorTextBase: '#e7eaf3',
            colorTextSecondary: '#8b90a3',
            colorTextTertiary: '#8b90a3',
            colorPrimaryBg: 'rgba(91, 140, 255, 0.1)',
            colorPrimaryBorder: 'rgba(91, 140, 255, 0.3)',
            colorSuccessBg: 'rgba(56, 217, 169, 0.1)',
            colorSuccessBorder: 'rgba(56, 217, 169, 0.3)',
            colorWarningBg: 'rgba(255, 209, 102, 0.1)',
            colorWarningBorder: 'rgba(255, 209, 102, 0.3)',
            colorErrorBg: 'rgba(255, 107, 107, 0.1)',
            colorErrorBorder: 'rgba(255, 107, 107, 0.3)',
            colorInfoBg: 'rgba(91, 140, 255, 0.1)',
            colorInfoBorder: 'rgba(91, 140, 255, 0.3)',
          } : {
            colorBgBase: '#ffffff',
            colorBgContainer: '#f8f9fa',
            colorBgElevated: '#ffffff',
            colorBorder: '#e9ecef',
            colorTextBase: '#333333',
            colorTextSecondary: '#6c757d',
            colorTextTertiary: '#6c757d',
          }),
        },
        components: {
          Upload: {
            pictureCardSize: 156,
          },
          Button: {
            primaryShadow: isDark 
              ? '0 2px 8px rgba(91, 140, 255, 0.25)' 
              : '0 2px 8px rgba(91, 140, 255, 0.35)',
          },
        },
      },
    });
    // 注意：setAntdConfig 是稳定的引用，不需要加入依赖数组
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);

  return {
    bgLayout: isDark ? '#151a27' : '#f0f2f5',
    actionsRender: () => [],
    avatarProps: {
      title: <div className={styles.avatar}>
        <Avatar
          size="small"
          style={{ backgroundColor: '#ffbf00', marginRight: 8 }}
          icon={<UserOutlined />} />
        <AvatarName />
      </div>,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    menu: {
      params: {
        userId: initialState?.currentUser?.id,
      },
      request: async () => {
        const data = loopMenuItems(transformRoute(routes).menuData);
        patchRoutes({ routes: data });
        return data;
      },
    },
    menuHeaderRender: undefined,
    ...initialState?.settings,
  };
};
